















import { Component, Prop, Watch, Vue } from "vue-property-decorator";

import { GranularityRange, RangeSlot, rangeIndices } from "./automaticReport";

// Reusable [head, tail] range selector over a fixed set of slots (7 weekdays for
// the `day` kind, 12 months for `month`). Selection is TWO-CLICK:
//   1st click -> head only (range INCOMPLETE, emits null)
//   2nd click -> commit [head, tail] (emits the pair)
//   3rd click -> reset to a new head (incomplete again)
// Ranges are inclusive and may wrap forward (e.g. [5, 3] = Sat..next Tue).
@Component
export default class GranularityRangePicker extends Vue {
  @Prop({ default: null }) readonly value!: GranularityRange | null;
  @Prop({ required: true }) readonly slots!: RangeSlot[];

  head: number | null = null;
  tail: number | null = null;

  created() {
    this.hydrate(this.value);
  }

  @Watch("value")
  onValueChange(next: GranularityRange | null) {
    // Ignore the echo of our own emit; only re-hydrate on a genuinely different
    // value (e.g. an external reset or an edit precompile).
    if (!this.rangesEqual(next, this.committed)) {
      this.hydrate(next);
    }
  }

  private get base(): number {
    return this.slots.length;
  }

  private get isComplete(): boolean {
    return this.head !== null && this.tail !== null;
  }

  // The currently committed range, or null while incomplete / empty.
  private get committed(): GranularityRange | null {
    return this.head !== null && this.tail !== null
      ? [this.head, this.tail]
      : null;
  }

  private get inRange(): Set<number> {
    if (this.head === null || this.tail === null) return new Set();
    return new Set(rangeIndices(this.head, this.tail, this.base));
  }

  slotClass(index: number) {
    const isEndpoint = index === this.head || index === this.tail;
    return {
      "granularity-range-picker__slot--endpoint": isEndpoint,
      "granularity-range-picker__slot--in-range":
        !isEndpoint && this.inRange.has(index),
    };
  }

  onSlotClick(index: number) {
    if (this.head === null || this.isComplete) {
      // Start (or restart) a selection: head only, incomplete.
      this.head = index;
      this.tail = null;
      this.$emit("input", null);
      return;
    }
    // Second click: commit the pair.
    this.tail = index;
    this.$emit("input", [this.head, this.tail] as GranularityRange);
  }

  private hydrate(range: GranularityRange | null) {
    if (range && range.length === 2) {
      this.head = range[0];
      this.tail = range[1];
    } else {
      this.head = null;
      this.tail = null;
    }
  }

  private rangesEqual(
    a: GranularityRange | null,
    b: GranularityRange | null
  ): boolean {
    if (a === null || b === null) return a === b;
    return a[0] === b[0] && a[1] === b[1];
  }
}
